import { HTTPError } from 'ky';
import { useEffect, useState } from 'react';

import { useResendVerificationEmail } from '@/features/auth/api/useResendVerificationEmail';
import { useAuthStore } from '@/features/auth/store/authStore';
import { useGetMe } from '@/features/settings/api/useGetMe';

// 60 s matches the backend `verify-resend` policy in
// RateLimitingExtensions.cs (PermitLimit = 1, Window = 1 min). Keeping the
// client value here lets the UI prevent the user from hitting a 429 in the
// first place; the catch on HTTPError 429 still re-arms the cooldown if a
// second tab raced and consumed the window.
const COOLDOWN_MS = 60_000;

// sessionStorage so a page refresh doesn't wipe the cooldown UI while the
// server still remembers the prior send. Keyed by user id so a different
// user signing in on the same browser starts fresh.
const STORAGE_KEY = (userId: string) => `signcare.resend-cooldown:${userId}`;

function loadCooldown(userId: string | undefined): number | null {
  if (!userId || typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY(userId));
    if (!raw) return null;
    const endsAt = Number(raw);
    if (!Number.isFinite(endsAt) || endsAt <= Date.now()) {
      window.sessionStorage.removeItem(STORAGE_KEY(userId));
      return null;
    }
    return endsAt;
  } catch {
    return null;
  }
}

function saveCooldown(userId: string | undefined, endsAt: number | null): void {
  if (!userId || typeof window === 'undefined') return;
  try {
    if (endsAt === null) {
      window.sessionStorage.removeItem(STORAGE_KEY(userId));
    } else {
      window.sessionStorage.setItem(STORAGE_KEY(userId), String(endsAt));
    }
  } catch {
    /* sessionStorage unavailable (private mode quota etc.) — fall back to
       in-memory only; the banner still works for this tab session. */
  }
}

// Full-width solid-red alert bar pinned to the top of authenticated pages
// above the sidebar + topbar (see AppShell). Shown whenever the current
// user has not yet verified their email.
//
// Visual spec per Figma node 2371:10454:
//   * Background: --color-alert-bg (#BF3636)
//   * Foreground: white
//   * Left-aligned copy; "registered email ID" is SemiBold + underlined
//   * No icon, no border
//
// The Resend control is a SignCare addition (not in the Figma node) — kept
// on the right so the user has a self-service path without exiting the
// alert. Clicking starts a 60 s client-side cooldown so the button stays
// disabled until the backend's per-user rate-limit window resets; a server
// 429 also arms the cooldown (covers multi-tab races where a second tab
// already consumed the window).
//
// Sticky-until-verified by design — the user opted out of a dismiss
// button so the nag persists across page renders. `role="alert"`
// announces the message to assistive tech when the banner mounts.
export function EmailVerificationBanner() {
  const user = useAuthStore((s) => s.user);
  const meQuery = useGetMe();
  const resend = useResendVerificationEmail();
  // Lazy init reads any active cooldown out of sessionStorage so a hard
  // refresh keeps the wait UI in sync with the server's window.
  const [cooldownEndsAt, setCooldownEndsAtState] = useState<number | null>(() =>
    loadCooldown(user?.id),
  );
  const [now, setNow] = useState<number>(() => Date.now());

  const setCooldownEndsAt = (value: number | null) => {
    setCooldownEndsAtState(value);
    saveCooldown(user?.id, value);
  };

  // If the user changes (logout + login), reload the cooldown from storage
  // so we never show another user's wait state.
  useEffect(() => {
    setCooldownEndsAtState(loadCooldown(user?.id));
  }, [user?.id]);

  // Tick once per second while the cooldown is active; stop the interval
  // as soon as it ends so we're not running a timer on every mounted page.
  useEffect(() => {
    if (cooldownEndsAt === null) return;
    if (Date.now() >= cooldownEndsAt) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [cooldownEndsAt]);

  // Prefer the /me query as the authoritative source -- the JWT-derived
  // `user.isEmailVerified` won't flip until the token is re-issued, but
  // /me reads straight from the DB and so reflects a fresh post-verify
  // state across page reloads. Fall back to the JWT flag only while
  // /me is still loading on the very first render.
  const verified = meQuery.data?.isEmailVerified ?? user?.isEmailVerified ?? false;

  if (!user || verified) {
    return null;
  }

  const remainingSeconds = cooldownEndsAt
    ? Math.max(0, Math.ceil((cooldownEndsAt - now) / 1000))
    : 0;
  const onCooldown = remainingSeconds > 0;
  const buttonDisabled = resend.isPending || onCooldown;

  const handleResend = () => {
    resend.mutate(undefined, {
      onSuccess: () => setCooldownEndsAt(Date.now() + COOLDOWN_MS),
      onError: (err) => {
        if (err instanceof HTTPError && err.response.status === 429) {
          setCooldownEndsAt(Date.now() + COOLDOWN_MS);
        }
      },
    });
  };

  const status =
    resend.isSuccess && onCooldown
      ? `Sent. Try again in ${remainingSeconds}s.`
      : resend.isError
        ? resend.error instanceof HTTPError && resend.error.response.status === 429
          ? `Too many attempts. Try again in ${remainingSeconds}s.`
          : 'Could not send. Try again in a minute.'
        : null;

  const buttonLabel = resend.isPending
    ? 'Sending…'
    : onCooldown
      ? `Wait ${remainingSeconds}s`
      : 'Resend';

  return (
    <div
      role="alert"
      className="w-full bg-alert-bg text-alert-foreground px-4 sm:px-9 py-4 flex items-center gap-4"
    >
      <p className="text-base font-medium flex-1 min-w-0">
        We have sent a code to your{' '}
        <span className="font-semibold underline underline-offset-2">registered email ID</span>.
        Please verify.
        {status && <span className="ms-2 font-medium opacity-90">{status}</span>}
      </p>
      <button
        type="button"
        onClick={handleResend}
        disabled={buttonDisabled}
        aria-busy={resend.isPending}
        className="text-sm font-semibold underline underline-offset-2 hover:no-underline disabled:opacity-60 disabled:no-underline disabled:cursor-not-allowed shrink-0"
      >
        {buttonLabel}
      </button>
    </div>
  );
}
